import React, { ReactNode } from 'react'
import { graphql } from 'gatsby'
import tw, { TwStyle } from 'twin.macro'
import Link from '@/components/link-component'
import colors from '@/constants/colors'

export const fragment = graphql`
  fragment CtaLink on ContentfulComponentCtaLink {
    id
    contentful_id
    __typename
    label
    link {
      url
    }
    size
    variant: style
    display
    icon {
      ...Icon
    }
    iconPosition
    plausibleEventName
  }
`

export const buttonStyles = {
  btnArrow: 'Arrow Link',
  btnOutlined: 'Button: Outlined',
  btnSolid: 'Button: Solid',
  btnPlain: 'Plain Link',
}

const { btnSolid, btnOutlined, btnArrow, btnPlain } = buttonStyles
const { primaryBlue, lightGray, indigoBlue, white, transparent } = colors

export type DisplayType = 'Inline' | 'Block'
export type AlignType = 'Left' | 'Center'
export type SizeType = 'Base' | 'Smaller' | 'Larger'
export type VariantType = typeof btnArrow | typeof btnOutlined | typeof btnSolid | typeof btnPlain

export type CtaLinkProps = {
  asButton?: boolean
  className?: string
  to?: string
  children?: ReactNode
  display?: DisplayType | string
  size?: SizeType
  align?: AlignType
  variant?: VariantType
  backgroundColor?: string
  icon?: {
    unicode: string
  }
  iconPosition?: string
  scrollToOffset?: number
  color?: string
  onClick?: React.MouseEventHandler
  css?: TwStyle | TwStyle[]
  plausibleEventName?: string
}

export default function CtaLink({
  to,
  asButton = false,
  children,
  display = 'Inline',
  size = 'Base',
  variant = btnArrow,
  backgroundColor = white,
  align = 'Left',
  icon = null,
  iconPosition = 'Left',
  css,
  className: _className,
  plausibleEventName,
  ...other
}: CtaLinkProps): JSX.Element {
  const hoverFocusStyle = 'bg-deepBlue text-white border-deepBlue'
  const btnHoverStyle = `group-hover:(${hoverFocusStyle}) hover:(${hoverFocusStyle}) focus:(outline-none ${hoverFocusStyle})`
  const btnStyle = [
    tw`transition-[background-color, color, box-shadow, border-color] ease-in-out duration-300`,
    align === 'Center' && tw`mx-auto`,
    size === 'Smaller' && tw`px-4 py-2 text-base h-[46px]`,
    size === 'Base' && tw`px-6 py-3 text-lg`,
    size === 'Larger' && tw`px-8 py-4 text-[21px]`,
    display === 'Inline' && tw`inline-block`,
    display === 'Block' && tw`table`,
    (btnArrow === variant || btnPlain === variant) && tw`px-0 hover:(text-indigoBlue)`,
    variant.includes('Button') && tw`border-2 border-solid rounded-full text-center`,
    (btnArrow === variant || btnPlain === variant) &&
      ([primaryBlue, indigoBlue] as string[]).includes(backgroundColor) &&
      tw`text-white hover:(text-gray-100)`,
    btnPlain === variant && tw`underline py-0`,
    btnSolid === variant &&
      ([primaryBlue, indigoBlue] as string[]).includes(backgroundColor) &&
      tw`bg-white border-white text-primaryBlue ${btnHoverStyle} dark:(text-gray-800 border-white bg-white hover:(bg-transparent text-white border-white))`,
    btnSolid === variant &&
      ([lightGray, white, transparent] as string[]).includes(backgroundColor) &&
      tw`bg-primaryBlue border-primaryBlue text-white ${btnHoverStyle} dark:(text-gray-800 border-white bg-white hover:(bg-transparent text-white border-white))`,
    btnOutlined === variant &&
      ([primaryBlue, indigoBlue] as string[]).includes(backgroundColor) &&
      tw`border-white text-white ${btnHoverStyle} dark:(border-white text-white)`,
    btnOutlined === variant &&
      ([lightGray, white, transparent] as string[]).includes(backgroundColor) &&
      tw`border-primaryBlue text-primaryBlue ${btnHoverStyle} dark:(border-white text-white hover:(bg-white text-gray-800 border-white))`,
    // tw`dark:(text-gray-100 border-gray-100 bg-transparent hover:bg-white hover:text-gray-800 hover:border-gray-800)`,
    css,
  ]

  const renderBtnText = () => (
    <span tw="flex items-center">
      {icon && iconPosition === 'Left' && (
        <span tw="font-bwi mr-2 !text-[16px]">{String.fromCodePoint(parseInt(icon.unicode, 16))}</span>
      )}
      {children}
      {btnArrow === variant && <span tw="font-bwi ml-2 !text-[16px]">&#xe91b;</span>}
      {icon && iconPosition === 'Right' && (
        <span tw="font-bwi ml-2 !text-[16px]">{String.fromCodePoint(parseInt(icon.unicode, 16))}</span>
      )}
    </span>
  )

  let className = _className || ''
  if (plausibleEventName?.trim()) {
    className += ` ${plausibleEventName}`
  }

  // option for rendering as a button, useful if make cards links (no nested anchor error...)
  return asButton ? (
    <button
      type="button"
      aria-label={`go to ${to}`}
      data-testid="cta-link"
      css={btnStyle}
      className={className.trim()}
      {...other}
    >
      {renderBtnText()}
    </button>
  ) : (
    <Link
      aria-label={`go to ${to}`}
      to={to}
      data-testid="cta-link"
      css={btnStyle}
      className={className.trim()}
      {...other}
    >
      {renderBtnText()}
    </Link>
  )
}
